// ---------------------------------------------------------------------------
// An aquifer's own balance — Acuiferos.cs's calcula_acu(), the exact same
// recession formula as Témez's own aquifer (temez.ts), applied at aquifer
// level instead of sub-basin level:
//
//   vf = Vo·e^(-alfa) + (Infiltr-bomb)·e^(-alfa/2)
//   qs = Vo - vf + Infiltr - bomb
//
// Both floored at 0, UNLESS `allowNegative` (Op_neg_acu/"Negativos en
// acuíferos", CalculationOptions.negativeAquifers) is checked — in which
// case neither is clamped at all (`Acuiferos.cs:100-123`: `if
// (G.op_neg_acu==0) { vf=Max(0,...); qs=Max(0,...); } else { vf=...; qs=...;
// }`, no clamp in the `else` branch).
//
// Unlike a sub-basin (mm, scaled by its own area), the aquifer's `Vo`/`Alfa`
// already work in VOLUME directly (`Acuiferos.cs:11,17`: "Volumen a final de
// momento temporal") — an aquifer has no area of its own in this model (see
// types.ts's Aquifer — no `area` field). `infiltration` (the aquifer's own
// `Infiltr`, "Entradas al acuífero por escorrentías subterráneas de las
// cuencas", `Acuiferos.cs:15`) is therefore expected ALREADY in volume
// units too — the same `groundwaterVolume` aggregation.ts's
// aggregateContributionsByAquifer already produces for the outlet/zone
// aggregates (mm × area × coefficient), not a raw mm series — the caller
// must aggregate across every linked sub-basin BEFORE calling this, this
// function has no notion of sub-basins at all.
//
// Pumping (`bomb`): the aquifer's own resolved series (Aquifer.pumpingCol),
// gated the same way as everywhere else in this app by "Modelar bombeos"
// (Op_bombeos/CalculationOptions.modelPumping) — `null` when unchecked or
// unloaded, same convention as every other model's own `pumping` parameter.
// ---------------------------------------------------------------------------

interface AquiferBalanceResult {
  /** Vf (volume): the aquifer's own storage after each step. */
  storage: Float64Array;
  /** Qs (volume): the aquifer's own discharge each step — this is its "aportación", added into whichever outlet point it's assigned to (see aggregation.ts). */
  discharge: Float64Array;
}

/**
 * Simulates one aquifer's own balance across the whole series.
 * `infiltration`/`pumping` must already be aligned step by step (same
 * length, same calendar) with each other and with the sub-basin results
 * they were aggregated from.
 */
export function calculateAquiferBalance(
  infiltration: Float64Array,
  pumping: Float64Array | null,
  vo: number,
  alfa: number,
  allowNegative: boolean
): AquiferBalanceResult {
  const n = infiltration.length;
  const storage = new Float64Array(n);
  const discharge = new Float64Array(n);

  let v = vo;

  for (let t = 0; t < n; t++) {
    const infiltr = Number.isFinite(infiltration[t]) ? infiltration[t] : 0;
    const bomb = pumping && Number.isFinite(pumping[t]) ? pumping[t] : 0;

    // vf is clamped (unless allowNegative) BEFORE computing qs, not after —
    // Acuiferos.cs:108-110 computes Acu[j].qs from the already-clamped
    // Acu[j].vf, not from the raw pre-clamp value.
    const rawVf = v * Math.exp(-alfa) + (infiltr - bomb) * Math.exp(-alfa / 2);
    const vf = allowNegative ? rawVf : Math.max(0, rawVf);
    const rawQs = v - vf + infiltr - bomb;
    const qs = allowNegative ? rawQs : Math.max(0, rawQs);

    storage[t] = vf;
    discharge[t] = qs;
    v = vf;
  }

  return { storage, discharge };
}
